import CustomLoadingButton from 'Components/CustomLoadingButton'
import InfoTooltip from 'Components/InfoTooltip'
import ProductTariffFieldError from 'Components/ProductTariffFieldErrors'
import MaximumDeviceTransfersTooltip from 'Services/ProductManagement/Features/Product/Components/MaximumDeviceTransfersTooltip'
import MaximumTripsPerDayInfoTooltip from 'Services/ProductManagement/Features/Product/Components/MaximumTripsPerDayInfoTooltip'
import { SELECTED_PRODUCT_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Hooks/useProductsWithParams'
import {
  createSingleProduct,
  type Product,
  ProductUsages,
  updateSingleProduct
} from 'Services/ProductManagement/Features/Product/productSlice'
import { type LoadingStates, type Translation, useAppDispatch } from 'Store'
import type { ProductTariffError, ProductTariffViolation } from 'Util/ApiClient'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

type FieldErrors = {
  [key: string]: string[]
}

const fieldErrorsDefaultState: FieldErrors = {
  sharedUsage: [],
  maxTripsPerDay: [],
  maxDeviceTransfers: [],
  maxDaysPerProduct: [],
  maxTripsPerProduct: []
}

interface WalttiTariffProductFormProps {
  product?: Product
  successBaseUrl: string
  submitLoading: LoadingStates
}

export default function WalttiTariffProductForm({
  product,
  successBaseUrl,
  submitLoading
}: Readonly<WalttiTariffProductFormProps>) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isEdit = !!product

  const [productDetails, setProductDetails] = useState<Partial<Product>>(
    (isEdit && product) || {
      activation_window: { duration: null, period: [] }
    }
  )
  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress handleProductISODurations
  useEffect(() => {
    if (isEdit && product) {
      setProductDetails(product)
    }
  }, [product])

  const handleChange = (
    value: string | string[] | Translation[] | number | boolean | undefined | null,
    field: keyof Product
  ) => {
    if (value !== undefined) {
      setProductDetails(prevState => ({ ...prevState, [field]: value }) as Product)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const productData = {
      ...productDetails
    }

    if (product) {
      dispatch(updateSingleProduct({ ...productData, id: product.id }))
        .then(unwrapResult)
        .then(() => {
          SnackbarUtils.success(t`Product successfully updated.`)
          navigate(successBaseUrl)
        })
        .catch(({ violations }: ProductTariffError) => {
          if (violations && violations.length > 0) {
            setValidationErrors(violations)
          }
          SnackbarUtils.error(t`There was an error updating the product.`)
        })
      return
    }
    dispatch(createSingleProduct(productData))
      .then(unwrapResult)
      .then((productResponse: Product) => {
        SnackbarUtils.success(t`Product successfully created.`)
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [SELECTED_PRODUCT_PARAMETER_KEY]: productResponse.id
          })
        )
      })
      .catch(({ violations }: ProductTariffError) => {
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        }
        SnackbarUtils.error(t`There was an error creating the product.`)
      })
  }

  function setValidationErrors(violations: ProductTariffViolation[]) {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[violation.field] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  const sharedUsageOptions = [
    {
      label: t`Single user`,
      value: ProductUsages.SINGLE_USER
    },
    {
      label: t`Concurrent users`,
      value: ProductUsages.CONCURRENT_USERS
    }
  ]

  const isSubmitLoading = submitLoading === 'pending'

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 240 }} required>
              <InputLabel size="small">{t`Shared usage`}</InputLabel>
              <Select
                name="shared_usage"
                required
                size="small"
                label={t`Shared usage`}
                value={productDetails?.shared_usage ?? ''}
                onChange={e => handleChange(e.target.value, 'shared_usage')}
              >
                {sharedUsageOptions.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <ProductTariffFieldError error={fieldErrors.sharedUsage} identifier="shared_usage" />
            </FormControl>
            <Box sx={{ p: 1 }}>
              <InfoTooltip
                title={
                  <Typography variant="body2">
                    {t`The value determines whether the product can be used only by the travel account holder or also by fellow passengers.`}
                  </Typography>
                }
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <FormControl sx={{ minWidth: 240 }} size="small">
              <TextField
                name="max_trips_day"
                label={t`Maximum trips per day`}
                size="small"
                type="number"
                value={productDetails?.max_trips_per_day ?? ''}
                error={fieldErrors.maxTripsPerDay?.length > 0}
                onChange={e =>
                  handleChange(
                    e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                    'max_trips_per_day'
                  )
                }
              />
              <ProductTariffFieldError
                error={fieldErrors.maxTripsPerDay}
                identifier="max_trips_per_day"
              />
            </FormControl>
            <Box sx={{ p: 1 }}>
              <MaximumTripsPerDayInfoTooltip />
            </Box>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <FormControl sx={{ minWidth: 240 }} size="small">
                <TextField
                  name="max_device_transfers"
                  label={t`Maximum device transfers`}
                  size="small"
                  type="number"
                  value={productDetails?.max_device_transfers ?? ''}
                  error={fieldErrors.maxDeviceTransfers?.length > 0}
                  onChange={e =>
                    handleChange(
                      e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                      'max_device_transfers'
                    )
                  }
                />
                <ProductTariffFieldError
                  error={fieldErrors.maxDeviceTransfers}
                  identifier="max_device_transfers"
                />
              </FormControl>
              <Box sx={{ p: 1 }}>
                <MaximumDeviceTransfersTooltip />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <FormControl sx={{ minWidth: 240 }} size="small">
                <TextField
                  name="max_days_per_product"
                  label={t`Maximum days per product`}
                  size="small"
                  type="number"
                  value={productDetails?.max_days_per_product ?? ''}
                  error={fieldErrors.maxDaysPerProduct?.length > 0}
                  onChange={e =>
                    handleChange(
                      e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                      'max_days_per_product'
                    )
                  }
                />
                <ProductTariffFieldError
                  error={fieldErrors.maxDaysPerProduct}
                  identifier="max_days_per_product"
                />
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 4 }}>
              <FormControl sx={{ minWidth: 240 }} size="small">
                <TextField
                  name="max_trips_per_product"
                  label={t`Maximum trips per product`}
                  size="small"
                  type="number"
                  value={productDetails?.max_trips_per_product ?? ''}
                  error={fieldErrors.maxTripsPerProduct?.length > 0}
                  onChange={e =>
                    handleChange(
                      e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                      'max_trips_per_product'
                    )
                  }
                />
                <ProductTariffFieldError
                  error={fieldErrors.maxTripsPerProduct}
                  identifier="max_trips_per_product"
                />
              </FormControl>
            </Box>
          </Box>
          <CustomLoadingButton
            isLoading={isSubmitLoading}
            type="submit"
            size="large"
            iconPosition="end"
          >
            {t`Save product`}
          </CustomLoadingButton>
        </Box>
      </Grid>
    </Grid>
  )
}
